import React from 'react'
import { Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'

import { colors, fontSizes } from '../../../constants/salesStyles'
import { WarehouseBreakdownItem } from '../../../types/sales/salesTypes'
import { adminRouteAlias, getAdminNavigationPath } from '../../../router/adminRouteAlias'
import { useSelector } from '../../../modules/store/customSelector'
import { getCurrencySymbol } from '../../../utils/getCurrencySymbol'

import styles from './styles.module.scss'

interface IProps {
    items: WarehouseBreakdownItem[]
}

const SalesWarehouseBreakdown = ({ items }: IProps) => {
    const currentCurrency = useSelector(state => state.partList.currentCurrency)
    const currencySymbol = getCurrencySymbol(currentCurrency)

    const getPartsLink = (warehouseId: string | null) => {
        const params = new URLSearchParams()
        params.set('wh', warehouseId === null ? 'null' : warehouseId)
        return `${getAdminNavigationPath(adminRouteAlias.parts.location)}?${params.toString()}`
    }

    if (!items.length) {
        return null
    }

    const formatCount = (value: number) => `${(value || 0).toLocaleString('ru-RU')} шт`
    const formatSum = (value: number) => `${(value || 0).toLocaleString('ru-RU')} ${currencySymbol}`

    const totals = items.reduce(
        (acc, item) => ({
            partCount: acc.partCount + (item.partCount || 0),
            partSum: acc.partSum + (item.partSum || 0),
            soldCount: acc.soldCount + (item.soldCount || 0),
            soldSum: acc.soldSum + (item.soldSum || 0),
        }),
        { partCount: 0, partSum: 0, soldCount: 0, soldSum: 0 },
    )

    return (
        <div className={styles.wrap}>
            <Table size="sm" className={styles.table}>
                <thead>
                    <tr>
                        <th rowSpan={2} className={styles.warehouseHead}>
                            Склад
                        </th>
                        <th colSpan={2} className={styles.groupHead}>
                            Наличие
                        </th>
                        <th colSpan={2} className={styles.groupHead}>
                            Продано
                        </th>
                    </tr>
                    <tr>
                        <th className={`${styles.alignRight} ${styles.groupStart}`}>Кол-во</th>
                        <th className={styles.alignRight}>Сумма</th>
                        <th className={`${styles.alignRight} ${styles.groupStart}`}>Кол-во</th>
                        <th className={styles.alignRight}>Сумма</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(item => (
                        <tr key={item.warehouseId ?? 'no-warehouse'}>
                            <td>
                                <Link
                                    to={getPartsLink(item.warehouseId)}
                                    className={styles.link}
                                    style={{ color: colors.blue, fontSize: fontSizes.s }}
                                >
                                    {item.title}
                                </Link>
                            </td>
                            <td className={`${styles.alignRight} ${styles.groupStart}`}>{formatCount(item.partCount)}</td>
                            <td className={styles.alignRight}>{formatSum(item.partSum)}</td>
                            <td className={`${styles.alignRight} ${styles.groupStart}`}>{formatCount(item.soldCount)}</td>
                            <td className={styles.alignRight}>{formatSum(item.soldSum)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td>Итого</td>
                        <td className={`${styles.alignRight} ${styles.groupStart}`}>{formatCount(totals.partCount)}</td>
                        <td className={styles.alignRight}>{formatSum(totals.partSum)}</td>
                        <td className={`${styles.alignRight} ${styles.groupStart}`}>{formatCount(totals.soldCount)}</td>
                        <td className={styles.alignRight}>{formatSum(totals.soldSum)}</td>
                    </tr>
                </tfoot>
            </Table>
        </div>
    )
}

export default SalesWarehouseBreakdown
